import { useMemo } from 'react';
import { array, option, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { EnterpriseProfilePrivileges as EnterpriseProfilePrivilegesEnum } from '@mews-commander/data-access';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { Order } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import {
    EnterpriseProfilePrivilegesEnumNames,
    EnterpriseProfilePrivilegesEnumValue,
    EnterpriseProfilePrivilegesFlagNames,
    EnterpriseProfilePrivileges,
} from '@mews-commander/legacy-data-structures';
import { privilegeFlagToEnumMap } from '@mews-commander/legacy-entities/src/utils';
import { useLocalizePrivilege } from '../../utils/useLocalizePrivilege';

export const usePrivilegesFilter = ({
    searchTerm,
    sortOrder,
}: {
    searchTerm?: string;
    sortOrder?: Order;
}): EnterpriseProfilePrivilegesEnumValue[] => {
    const overrideRestrictionsEnabled = useFeatureFlag(FeatureFlag.overrideRestrictionsEnabled);
    const sellLimitsEnabled = useFeatureFlag(FeatureFlag.sellLimitsEnabled);
    const manageMailTemplatesEnabled = useFeatureFlag(FeatureFlag.manageMailTemplatesPermissionEnabled);

    const localizePrivilege = useLocalizePrivilege();

    const privilegesEnumValues = useMemo(() => {
        const featureFlagDisabledEnumValues = new Set(
            compactArray([
                overrideRestrictionsEnabled ? null : EnterpriseProfilePrivileges.OverrideRestrictions,
                sellLimitsEnabled ? null : EnterpriseProfilePrivileges.ManageSellLimits,
                manageMailTemplatesEnabled ? null : EnterpriseProfilePrivileges.ManageMailTemplates,
            ])
        );

        return featureFlagDisabledEnumValues.size === 0
            ? EnterpriseProfilePrivileges.enumValues
            : EnterpriseProfilePrivileges.enumValues.filter((v) => !featureFlagDisabledEnumValues.has(v));
    }, [overrideRestrictionsEnabled, sellLimitsEnabled, manageMailTemplatesEnabled]);

    const privilegesFiltered = useMemo(
        () =>
            searchTerm
                ? privilegesEnumValues.filter(({ value: privilege }) => {
                      const text = searchTerm.toLowerCase();
                      const { label, hint } = localizePrivilege(privilege);

                      return label.toLowerCase().includes(text) || hint?.toLowerCase().includes(text);
                  })
                : privilegesEnumValues,
        [searchTerm, privilegesEnumValues, localizePrivilege]
    );

    const privilegesSorted = useMemo(() => {
        if (sortOrder == null) {
            return privilegesFiltered;
        }

        const orderByName: ord.Ord<EnterpriseProfilePrivilegesEnumValue> = pipe(
            sortOrder === Order.Descending ? ord.reverse(string.Ord) : string.Ord,
            ord.contramap(({ value: privilege }) => localizePrivilege(privilege).label.toLowerCase())
        );

        return array.sortBy([orderByName])(privilegesFiltered);
    }, [localizePrivilege, privilegesFiltered, sortOrder]);

    return privilegesSorted;
};

export const mapPrivilegesToRowSelection = (
    privileges: EnterpriseProfilePrivilegesEnum
): Record<EnterpriseProfilePrivilegesEnumNames, boolean> =>
    pipe(
        privileges,
        Object.entries,
        array.filterMap(([key, value]) =>
            key != null && privilegeFlagToEnumMap[key as EnterpriseProfilePrivilegesFlagNames] && value === true
                ? option.some([privilegeFlagToEnumMap[key as EnterpriseProfilePrivilegesFlagNames]?.value, value])
                : option.none
        ),
        Object.fromEntries
    );
